/**
 * The main-menu registry, plus the pure helpers that decide what a clinic sees
 * and where `/` lands.
 *
 * Lifted out of `Sidebar.tsx` so the admin Clinic Configuration page can offer
 * the item list without importing a component. Three consumers today: the
 * Sidebar (renders), `Header` (page title), and the dashboard module grid.
 *
 * ⚠️ Item `id`s are persisted in `clients.nav_hidden` / `clients.nav_landing`
 * (migration 0143) — treat them like column names. Renaming one silently
 * un-hides a menu item for every clinic that hid it. The SERVER never
 * interprets them; unknown ids fail soft here, which is what lets the columns
 * ship without server-side validation.
 *
 * Plan: docs/plans/2026-08-17-per-client-nav-and-landing.md
 */

import {
  ClipboardCheck,
  LayoutDashboard,
  Route,
  Phone,
  MessageSquare,
  Monitor,
  Tv,
  Calendar,
  CalendarDays,
  Star,
  FileText,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  path: string;
  comingSoon?: boolean;
}

/** Account settings — open to every signed-in user, a confined one included. */
export const SETTINGS_PATH = '/settings';

/** The Dashboard's own path, and the universal landing fallback.
 *
 *  It is the only route with no guard of any kind — no `ProtectedModule`, no
 *  `AdminOnly` — so it always renders something. `/` must never resolve to `/`
 *  (redirect loop), which is why the fallback is this and not the index. */
export const DASHBOARD_PATH = '/dashboard';

/** Dispatch is live once ANY interaction source can feed it. Mirrors the rule
 *  the sidebar has always used. */
const DISPATCH_SOURCES = ['ai-booking', 'chatbot', 'online-booking'];

export const MODULE_REGISTRY: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: DASHBOARD_PATH },
  // Directly after Dashboard and ABOVE Dispatch on purpose: this is the page a
  // receptionist lives on, and the sidebar is one flat list in registry order,
  // so registry position IS menu position.
  { id: 'front-desk', label: 'Front desk', description: "Today's self check-ins at your locations", icon: ClipboardCheck, path: '/front-desk' },
  { id: 'dispatch', label: 'Dispatch', description: 'View all calls, chats, and online bookings', icon: Route, path: '/dispatch' },
  { id: 'ai-booking', label: 'Ruby AI Booking', description: 'Voice booking funnel analytics', icon: Phone, path: '/ai-booking' },
  { id: 'chatbot', label: 'Chat Bot', description: 'Website chat bot analytics', icon: MessageSquare, path: '/chatbot' },
  { id: 'waiting-room', label: 'Waiting Room', description: 'Kiosk screenshots and message management', icon: Tv, path: '/waiting-room' },
  { id: 'calendar', label: 'Calendar', description: 'Room schedules, live from VELOX', icon: CalendarDays, path: '/calendar' },
  { id: 'online-booking', label: 'Online Booking', description: 'Online booking funnel analytics', icon: Calendar, path: '/online-booking' },
  { id: 'document-manager', label: 'Documents', description: 'Inbound faxes, requisitions, and uploaded documents', icon: FileText, path: '/documents' },
  { id: 'self-checkin', label: 'Self Check-In', description: 'Per-location check-in links + feature status', icon: Monitor, path: '/self-checkin' },
  { id: 'feedback', label: 'Feedback', description: 'Patient feedback and ratings', icon: Star, path: '/feedback', comingSoon: true },
];

/** The per-clinic menu settings, as they arrive on `ClinicInfo`. Both optional
 *  so a tab open across the deploy reads as "nothing configured". */
export interface NavSettings {
  navHidden?: string[];
  navLanding?: string | null;
}

/**
 * Is this item a live link for a clinic, as opposed to a greyed "Upgrade" row?
 *
 * Extracted from the Sidebar verbatim rather than reimplemented: the landing
 * resolver and the sidebar MUST agree, or `/` can resolve to a module the menu
 * is greying out.
 */
export function isNavItemEnabled(item: NavItem, enabledModules: string[]): boolean {
  const alwaysOn =
    item.id === 'dashboard' ||
    // The calendar reads the clinic's OWN VELOX schedule — it isn't a module a
    // clinic buys, so no `enabled_modules` row will ever name it. Without this
    // branch the row renders as a greyed "Upgrade" chip for every clinic user
    // (admins get a live link regardless, which is why both drift guards, which
    // run as admin, stayed green). It carries no permission gate either, so a
    // clinic that doesn't want the entry hides it via `nav_hidden`.
    item.id === 'calendar' ||
    // Front desk shows self check-ins and nothing else, so it lives or dies
    // with that module rather than being something a clinic buys separately.
    (item.id === 'front-desk' && enabledModules.includes('self-checkin')) ||
    (item.id === 'dispatch' && DISPATCH_SOURCES.some((id) => enabledModules.includes(id)));
  return alwaysOn || enabledModules.includes(item.id);
}

/** Did this clinic hide the item? Independent of who is looking — the SINGLE
 *  expression of the rule, so the render filter and the admin `Hidden` badge
 *  can't drift into two encodings of the same predicate. */
export function isNavItemHidden(item: NavItem, nav: NavSettings): boolean {
  return (nav.navHidden ?? []).includes(item.id);
}

/**
 * Does this item appear in the menu at all?
 *
 * ⚠️ Admins always see every item — the caller renders a `Hidden` badge instead.
 * That mirrors how an inactive module already shows admins an `Inactive` badge,
 * keeps an admin able to see what the clinic sees, and means the admin-driven
 * e2e sidebar drift guard is unaffected by any clinic's menu config.
 */
export function isNavItemVisible(item: NavItem, nav: NavSettings, isAdmin: boolean): boolean {
  return isAdmin || !isNavItemHidden(item, nav);
}

/**
 * The menu rows to render, in order: hidden entries dropped, then active
 * before inactive before coming-soon.
 *
 * ⚠️ Filtering happens BEFORE the rank, and that order is the whole point — a
 * hidden item must skip both the "live link" and the greyed "Upgrade" outcomes.
 * Merging the two would make a hidden Dispatch with no data source come back as
 * an Upgrade row, which is worse than not hiding it at all. It lives here rather
 * than inline in the Sidebar so the ordering is pinned by a test instead of by a
 * comment and a chain position.
 *
 * ⚠️ The sort must stay STABLE (`Array.prototype.sort` is) — that's what keeps
 * enabled items in registry order, and therefore keeps the top row equal to the
 * landing page `resolveLandingPath` picks when `navLanding` is unset. The
 * permission-gated exception to that agreement — the Calendar could top the menu
 * without being allowed to be the landing page — went away with the permission
 * on 2026-08-22.
 *
 * ⚠️ The two still part company when NOTHING is eligible: a clinic that hid both
 * the Dashboard and the Calendar and enabled no module tops out at a greyed
 * "Upgrade" row, while `resolveLandingPath` falls back to the Dashboard. That is
 * deliberate — landing staff on an Upgrade row is worse than landing them on a
 * hidden-but-working page — so do NOT "simplify" the resolver into
 * `visibleSortedNavItems(...)[0]`.
 */
export function visibleSortedNavItems(
  nav: NavSettings,
  enabledModules: string[],
  isAdmin: boolean,
  pages?: string[],
): NavItem[] {
  const rank = (item: NavItem): number => {
    if (item.comingSoon) return 2;
    return isNavItemEnabled(item, enabledModules) ? 0 : 1;
  };
  // A user confined to some pages sees exactly those — the clinic's hiding does
  // not apply, since for them the page list IS the menu.
  const shown = pages
    ? MODULE_REGISTRY.filter((item) => pages.includes(item.id))
    : MODULE_REGISTRY.filter((item) => isNavItemVisible(item, nav, isAdmin));
  return shown.sort((a, b) => rank(a) - rank(b));
}

/**
 * May a user confined to `pages` open this path? `/` (which redirects to their
 * landing page) and Account settings always; otherwise only an allowed page and
 * what sits below it. `App.tsx` sends anything else to `/`.
 */
export function isPathOpenToPages(pathname: string, pages: string[]): boolean {
  const under = (base: string) => pathname === base || pathname.startsWith(`${base}/`);
  if (pathname === '/' || under(SETTINGS_PATH)) return true;
  return MODULE_REGISTRY.some((item) => pages.includes(item.id) && under(item.path));
}

/**
 * Where `/` sends this clinic's users. A user confined to some `pages` (a
 * subrole such as Front Desk) lands on their own page instead — see
 * `confinedLandingPath`.
 *
 * ⚠️ Deliberately ignores `isAdmin`, unlike `isNavItemVisible`. An admin sees
 * the full menu (so they can work) but lands where the clinic lands (so they
 * see what the clinic sees). Do not unify the two.
 *
 * `comingSoon` items are never candidates — Feedback renders as a dead
 * "Coming Soon" row, so a clinic that hid everything above it would otherwise
 * land its staff on a blank page.
 *
 * Falling through re-checks eligibility at read time, so a landing page whose
 * module was later switched off self-heals to the next item rather than
 * greeting staff with "Module Not Available".
 */
export function resolveLandingPath(nav: NavSettings, enabledModules: string[], pages?: string[]): string {
  if (pages) return confinedLandingPath(enabledModules, pages);
  const hidden = new Set(nav.navHidden ?? []);
  const eligible = (item: NavItem): boolean =>
    !item.comingSoon && !hidden.has(item.id) && isNavItemEnabled(item, enabledModules);

  const explicit = nav.navLanding
    ? MODULE_REGISTRY.find((item) => item.id === nav.navLanding)
    : undefined;
  if (explicit && eligible(explicit)) return explicit.path;

  // No page is skipped here any more. Until 2026-08-22 the Calendar was
  // excluded, because it was always-on in the menu but gated by a permission —
  // so a clinic that hid the Dashboard could land a user without that
  // permission on an error banner as their home screen. The gate is gone, so
  // the exception is too, and the implicit landing page is once again exactly
  // the top row of `visibleSortedNavItems`.
  return MODULE_REGISTRY.find(eligible)?.path ?? DASHBOARD_PATH;
}

/**
 * `/` for a user confined to some pages: the first allowed page the clinic has
 * on, else the first allowed page regardless — which renders "Module Not
 * Available". ⚠️ It must never answer a path `isPathOpenToPages` refuses: the
 * guard would send that back to `/`, and `/` here again, forever. Settings is
 * the last resort for a list naming no page this build knows.
 */
function confinedLandingPath(enabledModules: string[], pages: string[]): string {
  const allowed = MODULE_REGISTRY.filter((item) => pages.includes(item.id) && !item.comingSoon);
  const live = allowed.find((item) => isNavItemEnabled(item, enabledModules));
  return (live ?? allowed[0])?.path ?? SETTINGS_PATH;
}
